import { useState, useEffect, useCallback } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Radio, Tv, Server, BarChart3, LogOut, ShieldCheck,
  Settings, Layers, Film, CalendarDays, Antenna, Zap, Package, Activity,
  FlaskConical, BadgeCheck, AlertTriangle, ScrollText, BellRing, Mail, Wrench,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import api from "@/lib/api";
import TopBar from "@/components/admin/TopBar";
import StripeSettingsModal from "@/components/admin/StripeSettingsModal";

const NAV_GROUPS = [
  {
    label: "Operasyon",
    items: [
      { to: "/admin",           end: true, label: "Pano",             icon: LayoutDashboard },
      { to: "/admin/analytics",            label: "Analitik",         icon: BarChart3 },
      { to: "/admin/nodes",                label: "Node & Load Balancing", icon: Server },
    ],
  },
  {
    label: "Müşteri",
    items: [
      { to: "/admin/customers", label: "Müşteriler", icon: Users },
      { to: "/admin/lines",     label: "Hatlar",     icon: Radio },
    ],
  },
  {
    label: "İçerik",
    items: [
      { to: "/admin/channels",    label: "Kanallar",         icon: Tv },
      { to: "/admin/categories",  label: "Kategoriler",      icon: Layers },
      { to: "/admin/sources",     label: "Stream Kaynakları",icon: Antenna },
      { to: "/admin/transcoding", label: "Transcoding",      icon: Zap },
      { to: "/admin/vod",         label: "VOD & Dizi",       icon: Film },
      { to: "/admin/bouquets",    label: "Paketler",         icon: Package },
      { to: "/admin/epg",         label: "EPG",              icon: CalendarDays },
    ],
  },
  {
    label: "Sistem",
    items: [
      { to: "/admin/notifications", label: "Bildirimler", icon: BellRing },
      { to: "/admin/marketing",     label: "Marketing",   icon: Mail },
      { to: "/admin/streams-live",  label: "Anlik Yayinlar", icon: Activity },
      { to: "/admin/tools",         label: "Araçlar",     icon: Wrench },
      { to: "/admin/audit",    label: "Audit Log", icon: ScrollText },
      { to: "/admin/settings", label: "Ayarlar",   icon: Settings },
    ],
  },
];

export default function AdminLayout({ children, breadcrumb = [], title }) {
  const { user, logout } = useAuth();
  const { panel_title, logo_url } = useSettings();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-bg">
      <Sidebar user={user} onLogout={handleLogout} panelTitle={panel_title} logoUrl={logo_url} />

      <main className="flex-1 min-w-0 flex flex-col pt-14 md:pt-0">
        <div className="hidden md:block">
          <TopBar title={title} breadcrumb={breadcrumb} />
        </div>
        {/* Mobile top bar */}
        <div className="md:hidden fixed top-0 inset-x-0 z-40 h-14 flex items-center justify-between px-4 border-b border-border bg-[#0a0a0a]/90 backdrop-blur">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-brand grid place-items-center"><ShieldCheck className="w-3.5 h-3.5" /></div>
            <span className="font-display font-bold" data-testid="layout-mobile-title">{panel_title}</span>
          </div>
          <button onClick={handleLogout} className="text-ink-dim" aria-label="Çıkış"><LogOut className="w-5 h-5" /></button>
        </div>
        <div className="flex-1">{children}</div>
      </main>
    </div>
  );
}

function Sidebar({ user, onLogout, panelTitle, logoUrl }) {
  return (
    <aside className="hidden md:flex w-[248px] shrink-0 flex-col border-r border-border bg-[#0a0a0a] sticky top-0 h-screen">
      <Link to="/admin" className="flex items-center gap-2.5 px-5 h-14 border-b border-border" data-testid="logo-link">
        <div className="relative w-8 h-8 rounded-md bg-gradient-to-br from-brand to-brand-hover grid place-items-center shadow-[0_0_20px_rgba(225,29,72,0.5)] overflow-hidden">
          {logoUrl
            ? <img src={logoUrl} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
            : <ShieldCheck className="w-4 h-4 text-white" />}
        </div>
        <div className="leading-tight">
          <div className="font-display font-bold text-base tracking-tight" data-testid="sidebar-panel-title">{panelTitle}</div>
          <div className="text-[9px] font-mono text-brand tracking-widest">IPTV CONTROL</div>
        </div>
      </Link>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5 scrollbar-thin" data-testid="main-nav">
        {NAV_GROUPS.map((g) => (
          <div key={g.label}>
            <div className="px-3 mb-1.5 text-[9px] font-mono uppercase tracking-widest text-ink-faint">
              {g.label}
            </div>
            <div className="space-y-0.5">
              {g.items.map(({ to, end, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  data-testid={`nav-${to.split("/").filter(Boolean).join("-") || "dashboard"}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition ${
                      isActive
                        ? "bg-brand/15 text-white border-l-2 border-brand"
                        : "text-ink-dim hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                    }`
                  }
                >
                  <Icon className="w-[17px] h-[17px]" />
                  <span className="flex-1">{label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <StripeModeBadge />

      {/* System status dock */}
      <div className="px-4 py-3 border-t border-border">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="w-3 h-3 text-ink-dim" />
          <span className="text-[10px] font-mono uppercase tracking-wider text-ink-dim">Sistem Durumu</span>
        </div>
        <div className="grid grid-cols-2 gap-1 text-[10px] font-mono">
          <div className="flex justify-between"><span className="text-ink-faint">CPU</span><span className="text-ink-dim">—</span></div>
          <div className="flex justify-between"><span className="text-ink-faint">MEM</span><span className="text-ink-dim">—</span></div>
          <div className="flex justify-between"><span className="text-ink-faint">Node</span><span className="text-ink-dim">—</span></div>
          <div className="flex justify-between"><span className="text-ink-faint">RTT</span><span className="text-ink-dim">—</span></div>
        </div>
      </div>

      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-3 p-2">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand to-brand-hover grid place-items-center text-sm font-bold">
            {user?.name?.[0]?.toUpperCase() || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name || "Admin"}</div>
            <div className="text-[10px] font-mono text-ink-dim uppercase tracking-wider">SÜPER ADMİN</div>
          </div>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-btn"
          className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-ink-dim hover:text-err hover:bg-err/10 transition"
        >
          <LogOut className="w-4 h-4" /> Çıkış Yap
        </button>
      </div>
    </aside>
  );
}


/* StripeModeBadge — Sidebar'da Stripe yapılandırma göstergesi.
   /api/saas/config/stripe public endpoint'inden mode okunur:
     • 'live'  → yeşil pill ("LIVE MODE")
     • 'test'  → amber pill ("TEST MODE")
     • 'unset' → kırmızı pill ("STRIPE YOK")
   Tıklayınca StripeSettingsModal açılır — operatör DB-tabanlı override'ı buradan
   yönetir (currency + price_id'ler, restart gerektirmez). */
function StripeModeBadge() {
  const [mode, setMode] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(() => {
    api.get("/saas/config/stripe").then((res) => {
      setMode(res.data?.mode ?? null);
    }).catch(() => { /* silent */ });
  }, []);

  useEffect(() => {
    let alive = true;
    api.get("/saas/config/stripe").then((res) => {
      if (alive) setMode(res.data?.mode ?? null);
    }).catch(() => { /* silent */ });
    return () => { alive = false; };
  }, []);

  if (!mode) return null;

  const palette = mode === "live"
    ? { bg: "bg-emerald-500/15", border: "border-emerald-500/40", text: "text-emerald-400", icon: BadgeCheck, label: "LIVE MODE", hint: "Gerçek ödemeler aktif" }
    : mode === "unset"
      ? { bg: "bg-red-500/15", border: "border-red-500/40", text: "text-red-400", icon: AlertTriangle, label: "STRIPE YOK", hint: "STRIPE_API_KEY tanımlanmamış" }
      : { bg: "bg-amber-500/15", border: "border-amber-500/40", text: "text-amber-400", icon: FlaskConical, label: "TEST MODE", hint: "Sandbox — gerçek ödeme yok" };

  const Icon = palette.icon;
  return (
    <>
      <div className="px-4 py-3 border-t border-border" data-testid="admin-stripe-mode" data-mode={mode}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`group relative w-full flex items-center gap-2 px-3 py-2 rounded-lg border transition hover:scale-[1.02] active:scale-[0.98] ${palette.bg} ${palette.border}`}
          data-testid="admin-stripe-mode-btn"
        >
          <Icon className={`w-3.5 h-3.5 shrink-0 ${palette.text}`} />
          <div className="min-w-0 flex-1 text-left">
            <div className={`text-[10px] font-mono font-bold uppercase tracking-wider ${palette.text}`}>
              {palette.label}
            </div>
            <div className="text-[9px] text-ink-faint truncate">{palette.hint}</div>
          </div>
          <Settings className={`w-3 h-3 shrink-0 opacity-50 ${palette.text}`} />

          {/* Hover tooltip — sağ tarafta küçük floating ipucu */}
          <span
            className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 rounded-md bg-slate-950 border border-slate-700 text-[10px] font-mono text-amber-400 whitespace-nowrap shadow-xl opacity-0 group-hover:opacity-100 transition-opacity z-50"
            data-testid="admin-stripe-mode-tooltip"
          >
            Stripe yapılandırmasını düzenle ↗
          </span>
        </button>
      </div>

      <StripeSettingsModal
        open={open}
        onClose={() => setOpen(false)}
        onSaved={() => refresh()}
      />
    </>
  );
}
